export interface License {
  id: string;
  type: string;
  number: string;
  issueDate: string;
  expiryDate: string;
}

export type AppStatus =
  | 'draft'
  | 'submitted'
  | 'video_pending'
  | 'video_completed'
  | 'under_review'
  | 'approved'
  | 'rejected'
  | 'waitlist';

export interface PersonalInfo {
  firstName: string;
  middleName?: string;
  lastName: string;
  preferredName?: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  language: 'English' | 'Spanish';
}

export interface Eligibility {
  isOver18: boolean;
  isAuthorizedToWork?: boolean;
}

// Florida-specific licensing
export interface FloridaLicensing {
  hasClassD: boolean;
  classDNumber?: string;
  classDExpiration?: string;
  hasClassG: boolean;
  classGNumber?: string;
  classGExpiration?: string;
}

// New York-specific licensing
export interface NewYorkLicensing {
  hasGuardCard: boolean;
  guardCardNumber?: string;
  guardCardExpiration?: string;
  has8HourCert: boolean;
  cert8HourDate?: string;
  has16HourCert: boolean;
  cert16HourDate?: string;
  hasFirearmCreds: boolean;
  firearmCredsNumber?: string;
  firearmCredsExpiration?: string;
}

// Common certifications
export interface CommonCertifications {
  hasCprAed: boolean;
  cprAedExpiration?: string;
  hasTwic: boolean;
  twicExpiration?: string;
}

export interface Licensing {
  licensingNotApplicable?: boolean;
  florida?: FloridaLicensing;
  newYork?: NewYorkLicensing;
  certifications: CommonCertifications;
  licenses: License[];
}

export interface Availability {
  roles: string[];
  locations: string[];
  shifts: string[];
  hasTransport: boolean;
  startDate: string;
}

export interface Preferences {
  indoorOutdoorComfort: number; // Scale 1-5 (1=prefer indoor, 5=prefer outdoor)
  standingPatrolTolerance: number; // Scale 1-5 (1=prefer stationary, 5=prefer patrol)
  languagesSpoken: string[];
  isVeteran?: boolean;
  // EEO Survey (optional, stored separately)
  eeoRace?: string;
  eeoGender?: string;
  eeoDisclosureConsent: boolean; // Whether they opted into EEO disclosure
}

export interface Consents {
  consentPrivacy: boolean;
  consentMessaging: boolean;
  consentVideoRecording: boolean;
  signature: string;
  signatureDate: string;
}

export interface ApplicationData {
  id: string;
  applicationId?: string; // APP-2024-XXX
  status: AppStatus;
  submittedAt?: string;
  updatedAt?: string;

  stepA: PersonalInfo;
  stepB: Eligibility;
  stepC: Licensing;
  stepD: Availability;
  stepE: Preferences;
  stepF: Consents;

  warnings?: string[]; // Non-blocking warnings
}

export const INITIAL_APPLICATION_DATA: ApplicationData = {
  id: '',
  status: 'draft',
  stepA: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    language: 'English',
  },
  stepB: {
    isOver18: false,
    isAuthorizedToWork: undefined,
  },
  stepC: {
    licensingNotApplicable: false,
    licenses: [],
    certifications: {
      hasCprAed: false,
      hasTwic: false,
    },
  },
  stepD: {
    roles: [],
    locations: [],
    shifts: [],
    hasTransport: false,
    startDate: '',
  },
  stepE: {
    indoorOutdoorComfort: 3,
    standingPatrolTolerance: 3,
    languagesSpoken: ['English'],
    eeoDisclosureConsent: false,
  },
  stepF: {
    consentPrivacy: false,
    consentMessaging: false,
    consentVideoRecording: false,
    signature: '',
    signatureDate: '',
  },
};
